import { useRef, useState, type ReactNode } from 'react';
import { Sparkline, fmt, type FmtKind } from '../ds';
import { Modal } from './Modal';
import { dowOf, periodLabel, type Tone } from './data';

/* ── the clickable sparkline ───────────────────────────────────────────────
 * Port of sparkTrend(). Every sparkline on this page is a button that opens the
 * full day chart, which is the whole reason the tables can stay this dense: the
 * 92x20 line says "something moved" and the modal says what.
 */
export interface TrendSpec {
  label: string;
  dates: string[];
  values: Array<number | null>;
  kind: FmtKind;
  /** One line under the modal title, saying what the series is. */
  subtitle?: string;
  /** The SQL-level definition, shown in the modal's foot. */
  formula?: string;
}

export function Spark({ spec, tone, w = 92, h = 20, onOpen }: {
  spec: TrendSpec;
  tone: Tone;
  w?: number;
  h?: number;
  onOpen: (spec: TrendSpec) => void;
}) {
  const clean = spec.values.filter((v): v is number => v !== null && Number.isFinite(v));
  // Never draw an unreadable stub: one point is not a trend, and a flat line
  // between two invented endpoints is worse than an empty cell.
  if (spec.dates.length < 2 || clean.length < 2) return null;
  return (
    <button
      className="spark-btn"
      title={`${spec.label} — click to open`}
      onClick={(e) => { e.stopPropagation(); onOpen(spec); }}
    >
      <Sparkline values={spec.values} tone={tone} w={w} h={h} />
    </button>
  );
}

/* ── the day chart ────────────────────────────────────────────────────────
 * One renderer, shared by the generic trend window and the SKU trend.
 *
 * THE Y AXIS IS FLOORED AT ZERO. A truncated baseline turns a 3% move into a
 * cliff, and this chart is read by people deciding what to do about the move.
 * Nulls are dropped from the polyline — the line closes the gap — but the day
 * still exists for the tooltip, which reads "not crawled".
 */
const W = 880;
const H = 250;

interface Pad { l: number; b: number; t: number; r: number }

function DayChart({ dates, values, kind, pad, marker }: {
  dates: string[];
  values: Array<number | null>;
  kind: FmtKind;
  pad: Pad;
  /** The day the crawl denominator steps up sharply, found from the data. */
  marker?: { i: number; label: string };
}) {
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);

  const clean = values.filter((v): v is number => v !== null && Number.isFinite(v));
  const mn = clean.length ? Math.min(...clean) : 0;
  const mx = clean.length ? Math.max(...clean) : 1;
  const p = (mx - mn) * 0.12 || 1;
  const lo = Math.max(0, mn - p);
  const hi = mx + p;
  const x = (i: number): number => pad.l + (i / (dates.length - 1 || 1)) * (W - pad.l - pad.r);
  const y = (v: number): number => pad.t + (1 - (v - lo) / (hi - lo)) * (H - pad.t - pad.b);
  const line = values
    .map((v, i) => (v === null ? null : `${x(i).toFixed(1)},${y(v).toFixed(1)}`))
    .filter((s): s is string => s !== null)
    .join(' ');
  const every = Math.max(1, Math.ceil(dates.length / 9));

  /**
   * The tooltip is positioned imperatively, exactly as wireHover() does it: the
   * clamp needs the tooltip's own rendered width, which is not knowable during
   * render. `left`/`top` are never passed through React's style prop, so there
   * is nothing for React to fight over.
   */
  const onMove = (e: React.MouseEvent<SVGRectElement>): void => {
    const svg = svgRef.current;
    if (!svg) return;
    const box = svg.getBoundingClientRect();
    const sx = (e.clientX - box.left) * (W / box.width);
    let best = 0;
    let bd = Infinity;
    dates.forEach((_, i) => {
      const d = Math.abs(x(i) - sx);
      if (d < bd) { bd = d; best = i; }
    });
    setHover(best);
    const tip = tipRef.current;
    if (tip) {
      const v = values[best];
      const px = (x(best) / W) * box.width;
      const py = ((v === null || v === undefined ? pad.t : y(v)) / H) * box.height;
      const tw = tip.offsetWidth || 120;
      tip.style.left = `${Math.max(4, Math.min(box.width - tw - 4, px - tw / 2))}px`;
      tip.style.top = `${Math.max(4, py - tip.offsetHeight - 12)}px`;
    }
  };

  const hv = hover === null ? null : values[hover] ?? null;
  const prevV = hover !== null && hover > 0 ? values[hover - 1] ?? null : null;
  const dayChange = hv !== null && prevV !== null && prevV !== 0
    ? ((hv - prevV) / Math.abs(prevV)) * 100
    : null;

  return (
    <div className="chart-wrap" style={{ position: 'relative' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        style={{ display: 'block' }}
        role="img"
        aria-label="Daily values"
      >
        {[0, 1, 2, 3, 4].map((k) => {
          const v = lo + ((hi - lo) * k) / 4;
          const yy = y(v);
          return (
            <g key={k}>
              <line x1={pad.l} x2={W - pad.r} y1={yy} y2={yy} stroke="var(--line)" />
              <text x={pad.l - 8} y={yy + 3.5} textAnchor="end" fontSize="9.5" fill="var(--ink-3)">
                {fmt(v, kind)}
              </text>
            </g>
          );
        })}
        {dates.map((d, i) => (i % every ? null : (
          <text key={d} x={x(i).toFixed(1)} y={H - pad.b + 15} textAnchor="middle" fontSize="9.5" fill="var(--ink-3)">
            {d.slice(5)}
          </text>
        )))}
        <polyline
          points={line}
          fill="none"
          stroke="var(--ink)"
          strokeWidth="1.9"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {values.map((v, i) => (v === null ? null : (
          <circle key={dates[i] ?? i} cx={x(i).toFixed(1)} cy={y(v).toFixed(1)} r="2.4" fill="var(--ink)" fillOpacity=".5">
            <title>{`${dates[i] ?? ''}: ${fmt(v, kind)}`}</title>
          </circle>
        )))}
        {marker ? (
          <g>
            <line
              x1={x(marker.i).toFixed(1)} x2={x(marker.i).toFixed(1)}
              y1={pad.t} y2={H - pad.b}
              stroke="var(--warn)" strokeDasharray="3 3"
            />
            <text x={(x(marker.i) + 5).toFixed(1)} y={pad.t + 10} fontSize="9" fill="var(--warn)">
              {marker.label}
            </text>
          </g>
        ) : null}
        {/* Blue marks the interaction, never a value — the one place on this
            page where the accent colour appears on a chart. */}
        <line
          x1={hover === null ? 0 : x(hover)}
          x2={hover === null ? 0 : x(hover)}
          y1={pad.t}
          y2={H - pad.b}
          stroke="var(--blue)"
          strokeWidth="1"
          opacity={hover === null ? 0 : 0.45}
        />
        <circle
          r="4"
          fill="var(--blue)"
          stroke="#fff"
          strokeWidth="1.6"
          cx={hover === null || hv === null ? 0 : x(hover)}
          cy={hover === null || hv === null ? 0 : y(hv)}
          opacity={hover === null || hv === null ? 0 : 1}
        />
        <rect
          x={pad.l} y={pad.t}
          width={W - pad.l - pad.r} height={H - pad.t - pad.b}
          fill="transparent"
          style={{ cursor: 'crosshair' }}
          onMouseMove={onMove}
          onMouseLeave={() => { setHover(null); }}
        />
      </svg>
      <div
        ref={tipRef}
        style={{
          position: 'absolute', pointerEvents: 'none',
          opacity: hover === null ? 0 : 1, transition: 'opacity .1s',
          background: 'var(--surface)', border: '1px solid var(--line)',
          borderRadius: 'var(--radius-control)', boxShadow: '0 4px 12px rgba(16,24,40,.10)',
          padding: '7px 10px', whiteSpace: 'nowrap', zIndex: 2,
        }}
      >
        <div style={{ fontSize: 10.5, color: 'var(--ink-3)', marginBottom: 1 }}>
          {hover === null ? '' : `${dates[hover] ?? ''} · ${dowOf(dates[hover] ?? '')}`}
        </div>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' }}>
          {hv === null ? 'not crawled' : fmt(hv, kind)}
        </div>
        <div style={{ fontSize: 10.5, marginTop: 2 }}>
          {dayChange === null ? (
            <span style={{ color: 'var(--ink-3)' }}>no previous day</span>
          ) : (
            <span style={{ color: dayChange > 0 ? 'var(--up)' : dayChange < 0 ? 'var(--down)' : 'var(--ink-3)' }}>
              {dayChange > 0 ? '+' : ''}{dayChange.toFixed(1)}% vs previous day
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── the five stat blocks above every chart ─────────────────────────────── */

function Stat({ label, children, color }: { label: string; children: ReactNode; color?: string }) {
  return (
    <div className="chart-stat">
      <div className="kpi-label">{label}</div>
      <div className="chart-stat-val" {...(color ? { style: { color } } : {})}>{children}</div>
    </div>
  );
}

/** Best / worst day, which the SKU trend renders smaller than the other three. */
function StatSmall({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="chart-stat">
      <div className="kpi-label">{label}</div>
      <div style={{ fontSize: 13, paddingTop: 5 }}>{children}</div>
    </div>
  );
}

interface Summary {
  last: number | null;
  chg: number | null;
  mean: number | null;
  best: { d: string; v: number } | null;
  worst: { d: string; v: number } | null;
}

/**
 * The window summary. `Change over window` is FIRST against LAST, not a mean of
 * daily changes — the question it answers is "where did this end up", and a
 * mean of daily percentage changes answers a different question badly.
 */
function summarise(dates: string[], values: Array<number | null>): Summary {
  const pairs = dates
    .map((d, i) => ({ d, v: values[i] ?? null }))
    .filter((x): x is { d: string; v: number } => x.v !== null && Number.isFinite(x.v));
  if (!pairs.length) return { last: null, chg: null, mean: null, best: null, worst: null };
  const first = pairs[0];
  const last = pairs[pairs.length - 1];
  if (!first || !last) return { last: null, chg: null, mean: null, best: null, worst: null };
  const best = pairs.reduce((a, b) => (b.v > a.v ? b : a));
  const worst = pairs.reduce((a, b) => (b.v < a.v ? b : a));
  return {
    last: last.v,
    chg: first.v ? ((last.v - first.v) / Math.abs(first.v)) * 100 : null,
    mean: pairs.reduce((a, b) => a + b.v, 0) / pairs.length,
    best,
    worst,
  };
}

function StatRow({ s, kind, small }: { s: Summary; kind: FmtKind; small: boolean }) {
  const chgColour = s.chg === null ? undefined
    : s.chg > 0 ? 'var(--up)' : s.chg < 0 ? 'var(--down)' : undefined;
  return (
    <div className="chart-stats">
      <Stat label="Latest">{fmt(s.last, kind)}</Stat>
      <Stat label="Change over window" {...(chgColour ? { color: chgColour } : {})}>
        {s.chg === null ? '—' : `${s.chg > 0 ? '+' : ''}${s.chg.toFixed(1)}%`}
      </Stat>
      <Stat label="Daily average">{fmt(s.mean, kind)}</Stat>
      {small ? (
        <>
          <StatSmall label="Best day">{s.best ? `${s.best.d.slice(5)} · ${fmt(s.best.v, kind)}` : '—'}</StatSmall>
          <StatSmall label="Worst day">{s.worst ? `${s.worst.d.slice(5)} · ${fmt(s.worst.v, kind)}` : '—'}</StatSmall>
        </>
      ) : (
        <>
          <Stat label="Best day">{s.best ? `${s.best.d.slice(5)} · ${fmt(s.best.v, kind)}` : '—'}</Stat>
          <Stat label="Worst day">{s.worst ? `${s.worst.d.slice(5)} · ${fmt(s.worst.v, kind)}` : '—'}</Stat>
        </>
      )}
    </div>
  );
}

/* ── the generic trend window ─────────────────────────────────────────────
 * Port of trendWindow(id). Opened by ANY sparkline on the page or inside
 * another modal, so it serves the KPI tiles, the geography rows, the portfolio
 * rows and the geo drill's daily-OSA column from one renderer.
 */
export function TrendModal({ spec, horizon, onClose }: {
  spec: TrendSpec;
  horizon: string;
  onClose: () => void;
}) {
  const s = summarise(spec.dates, spec.values);
  const first = spec.dates[0];
  const last = spec.dates[spec.dates.length - 1];
  return (
    <Modal
      title={spec.label}
      count={`${spec.dates.length} days · ${periodLabel(first, last)}`}
      onClose={onClose}
      foot={(
        <>
          <span>{spec.formula ?? 'Daily values over the selected period.'}</span>
          <span className="spacer" />
          <span>Data to {horizon}</span>
        </>
      )}
    >
      <div style={{ padding: '16px 18px' }}>
        {spec.subtitle ? (
          <div className="finding-meta" style={{ margin: '-4px 0 12px' }}>{spec.subtitle}</div>
        ) : null}
        <StatRow s={s} kind={spec.kind} small={false} />
        <DayChart
          dates={spec.dates}
          values={spec.values}
          kind={spec.kind}
          pad={{ l: 72, b: 32, t: 14, r: 16 }}
        />
      </div>
    </Modal>
  );
}

/* ── the SKU trend ────────────────────────────────────────────────────────
 * Port of trendHTML() / openTrend(sku). Two segmented controls, and a window
 * that is deliberately independent of the page's date picker: a SKU's
 * trajectory is worth seeing whole.
 */
export const TREND_WINDOWS = [7, 14, 30] as const;
export type TrendWindow = (typeof TREND_WINDOWS)[number];

export function SkuTrendModal({
  title, dates, values, kind, metric, win, onMetric, onWin, foot, horizon, marker, loadedDays, onClose,
}: {
  title: string;
  dates: string[];
  values: Array<number | null>;
  kind: FmtKind;
  metric: string;
  win: TrendWindow;
  onMetric: (m: string) => void;
  onWin: (w: TrendWindow) => void;
  foot: ReactNode;
  horizon: string;
  marker?: { i: number; label: string };
  /** How many days the page's own window actually loaded, for the caption. */
  loadedDays: number;
  onClose: () => void;
}) {
  const s = summarise(dates, values);
  const last = dates[dates.length - 1] ?? '—';
  return (
    <Modal
      title={title}
      count={`${dates.length} days to ${last}`}
      // The report reads a 90-day seed and can always honour 30d. In-app the
      // atoms only cover the window the page fetched, so the control is capped
      // by what is loaded and says so rather than silently showing fewer days
      // than its own label promises.
      fixedWindow={`fixed window · last ${String(Math.min(win, loadedDays))} of ${String(loadedDays)} loaded days, independent of the date picker`}
      onClose={onClose}
      foot={(
        <>
          <span>{foot}</span>
          <span className="spacer" />
          <span>Data to {horizon}</span>
        </>
      )}
    >
      <div style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <div className="seg">
            {([['gmv', 'GMV'], ['units', 'Units'], ['asp', 'ASP'], ['osa', 'OSA %']] as const).map(([k, l]) => (
              <button key={k} className={metric === k ? 'on' : ''} onClick={() => { onMetric(k); }}>{l}</button>
            ))}
          </div>
          <div className="seg">
            {TREND_WINDOWS.map((k) => (
              <button key={k} className={win === k ? 'on' : ''} onClick={() => { onWin(k); }}>{k}d</button>
            ))}
          </div>
        </div>
        <StatRow s={s} kind={kind} small />
        <DayChart
          dates={dates}
          values={values}
          kind={kind}
          pad={{ l: 68, b: 32, t: 12, r: 14 }}
          {...(marker ? { marker } : {})}
        />
      </div>
    </Modal>
  );
}
